import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { ArrowRight, Plus, Mail, CheckCircle2 } from "lucide-react";

import {
  PageHeader,
  PageContainer,
  Card,
  Input,
  Button,
  Badge,
  EmptyState,
} from "../components/ui";
import {
  INSTAGRAM_CREDITS,
  ONBOARDING_INSTAGRAM_INTEGRATION,
} from "../OnboardingPage/routeNames";
import useInstagramIntegrationHook from "../CustomHooks/useInstagramIntegrationHook";
import { callNonStoreApi, callNonStoreApiNew } from "../NonStoreApis";
import {
  InstagramIntegrationApi,
  UpdateProfile,
  FetchNotificationsPrefs,
  UpdateNotificationsPrefs,
  DeleteAccount,
} from "../Api/ApiEndPoints";
import userImg from "../ImageAssets/user.jpg";
import { isValidImage, isNullOrUndefined, logout } from "../utils";
import { updateApiUserDataAction } from "../Store/userSlice";
import { getStoreRef } from "../Store/persiststore";

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "instagram", label: "Instagram accounts" },
  { id: "notifications", label: "Notifications" },
  { id: "billing", label: "Billing" },
  { id: "danger", label: "Danger zone" },
];

function ProfileSettings() {
  const [active, setActive] = useState("profile");
  const { apiUserData } = useSelector((state) => state.user);

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle="Manage your account and preferences"
      />
      <PageContainer maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box sx={{ position: { md: "sticky" }, top: 80 }}>
              <Stack spacing={0.5}>
                {SECTIONS.map((s) => {
                  const isActive = active === s.id;
                  return (
                    <Box
                      key={s.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setActive(s.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setActive(s.id);
                        }
                      }}
                      sx={{
                        px: "12px",
                        py: "8px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? "#c2410c" : "#475569",
                        bgcolor: isActive ? "#fff7ed" : "transparent",
                        transition:
                          "background-color 150ms ease-out, color 150ms ease-out",
                        "&:hover": {
                          bgcolor: isActive ? "#fff7ed" : "#f8fafc",
                        },
                        "&:focus-visible": {
                          outline: "2px solid #f97316",
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {s.label}
                    </Box>
                  );
                })}
              </Stack>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => logout(getStoreRef())}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    logout(getStoreRef());
                  }
                }}
                sx={{
                  mt: "16px",
                  px: "12px",
                  py: "8px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#ef4444",
                  transition: "background-color 150ms ease-out",
                  "&:hover": { bgcolor: "#fef2f2" },
                  "&:focus-visible": { outline: "2px solid #f97316", outlineOffset: 2 },
                }}
              >
                Log out
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={9}>
            {active === "profile" && (
              <ProfileSection apiUserData={apiUserData} />
            )}
            {active === "instagram" && <InstagramSection />}
            {active === "notifications" && <NotificationsSection />}
            {active === "billing" && <BillingSection />}
            {active === "danger" && <DangerSection />}
          </Grid>
        </Grid>
      </PageContainer>
    </>
  );
}

export default ProfileSettings;

// -----------------------------------------------------------------------------
// Profile section
// -----------------------------------------------------------------------------
function ProfileSection({ apiUserData }) {
  const dispatch = useDispatch();
  const [name, setName] = useState(apiUserData?.Name || "");
  const [saving, setSaving] = useState(false);
  const existingEmail =
    apiUserData?.email || apiUserData?.Email || apiUserData?.userEmail || "";
  const [subscribeEmail, setSubscribeEmail] = useState(existingEmail);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(!!existingEmail);
  const phone =
    apiUserData?.phoneNumber ||
    apiUserData?.PhoneNumber ||
    apiUserData?.userNumber ||
    "";

  useEffect(() => {
    setName(apiUserData?.Name || "");
  }, [apiUserData]);
  useEffect(() => {
    setSubscribeEmail(existingEmail);
    setSubscribed(!!existingEmail);
  }, [existingEmail]);

  const isDirty = (name || "").trim().length > 0 && name !== (apiUserData?.Name || "");

  const handleSave = async () => {
    if (!isDirty || saving) return;
    try {
      setSaving(true);
      const response = await callNonStoreApiNew("put", UpdateProfile, {
        name: name.trim(),
      });
      if (response.status === 200 && response.data?.success) {
        // Merge updated name into Redux user slice so the rest of the app sees it.
        dispatch(
          updateApiUserDataAction({
            ...apiUserData,
            Name: response.data.user?.name ?? name.trim(),
          })
        );
        enqueueSnackbar("Profile updated", { variant: "success" });
      } else {
        enqueueSnackbar("Couldn't save profile", { variant: "error" });
      }
    } catch (err) {
      console.error("updateProfile failed:", err);
      enqueueSnackbar("Couldn't save profile", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscribeEmail.trim());

  const handleSubscribe = async () => {
    if (!validEmail || subscribing) return;
    try {
      setSubscribing(true);
      const response = await callNonStoreApiNew("put", UpdateProfile, {
        name: name.trim() || apiUserData?.Name || "",
        email: subscribeEmail.trim(),
        subscribed: true,
      });
      if (response.status === 200 && response.data?.success) {
        dispatch(
          updateApiUserDataAction({
            ...apiUserData,
            email: subscribeEmail.trim(),
          })
        );
        setSubscribed(true);
        enqueueSnackbar("Subscribed! You'll hear from us soon.", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Couldn't subscribe. Please try again.", {
          variant: "error",
        });
      }
    } catch (err) {
      console.error("subscribe failed:", err);
      enqueueSnackbar("Couldn't subscribe. Please try again.", {
        variant: "error",
      });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <Card padding="lg">
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
        Profile
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
        Update your personal information.
      </Typography>

      <Stack spacing={2.5} sx={{ mt: 3, maxWidth: 480 }}>
        <Input
          label="Display name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          placeholder="e.g. Aditi Rao"
        />
        <Input
          label="Phone"
          value={phone}
          disabled
          fullWidth
          size="small"
          helperText={
            phone
              ? "Phone is linked to your OTP login."
              : "No phone number on record."
          }
        />
      </Stack>

      {/* Newsletter subscribe prompt — styled as a marketing callout */}
      <Box
        sx={{
          mt: 4,
          maxWidth: 480,
          p: "20px",
          borderRadius: "12px",
          border: "1px solid #fed7aa",
          background: "linear-gradient(135deg, #fff7ed 0%, #ffffff 70%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: "#ffedd5",
              color: "#c2410c",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Mail size={16} strokeWidth={2.25} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, color: "#0f172a", lineHeight: 1.3 }}
            >
              Stay in the loop
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mt: "2px" }}>
              Get the latest updates, offers, and product tips in your inbox.
            </Typography>
          </Box>
        </Box>
        {subscribed ? (
          <Box
            sx={{
              mt: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: 13,
              color: "#047857",
            }}
          >
            <CheckCircle2 size={16} />
            <Box component="span" sx={{ fontWeight: 600 }}>
              Subscribed as {subscribeEmail}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              mt: "14px",
              display: "flex",
              gap: "8px",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Input
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              fullWidth
              size="small"
              placeholder="you@example.com"
              type="email"
            />
            <Button
              variant="primary"
              size="md"
              onClick={handleSubscribe}
              disabled={!validEmail || subscribing}
              loading={subscribing}
              sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
            >
              Subscribe
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={!isDirty || saving}
          loading={saving}
        >
          Update profile
        </Button>
      </Box>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Instagram accounts section
// -----------------------------------------------------------------------------
function InstagramSection() {
  const { accounts, handleSetSelectedAccountCredentials } =
    useInstagramIntegrationHook();
  const { apiUserData } = useSelector((state) => state.user);
  const { credentials } = useSelector((state) => state.instagram);
  const [connecting, setConnecting] = useState(false);

  const handleConnectAnother = async () => {
    try {
      setConnecting(true);
      const response = await callNonStoreApi(InstagramIntegrationApi, null, {
        params: { username: apiUserData?.userName },
      });
      window.location.href = response.data.instagramAuthUrl;
    } catch (error) {
      enqueueSnackbar("There is some error in connecting to Instagram", {
        variant: "error",
      });
      setConnecting(false);
    }
  };

  return (
    <Card padding="lg">
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
        Instagram accounts
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
        Manage the Instagram accounts linked to your workspace.
      </Typography>

      {accounts.length === 0 ? (
        <Box sx={{ mt: 3 }}>
          <EmptyState
            title="No Instagram accounts connected"
            description="Connect an Instagram account to start automating replies."
            action={
              <Button
                variant="primary"
                size="md"
                onClick={handleConnectAnother}
                loading={connecting}
                leftIcon={<Plus size={16} />}
              >
                Connect account
              </Button>
            }
          />
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 3 }}>
          {accounts.map((acc) => (
            <InstagramAccountRow
              key={acc.id || acc.user_id || acc.username}
              account={acc}
              isActive={credentials?.user_id === acc.user_id}
              onSelect={() => handleSetSelectedAccountCredentials(acc.username)}
              onReconnect={handleConnectAnother}
              reconnecting={connecting}
            />
          ))}
        </Stack>
      )}

    </Card>
  );
}

function InstagramAccountRow({ account, isActive, onSelect, onReconnect, reconnecting }) {
  const [photoUrl, setPhotoUrl] = useState(
    account?.profile_picture_url || userImg
  );

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (isNullOrUndefined(account?.profile_picture_url)) return;
      const ok = await isValidImage(account.profile_picture_url);
      if (!cancelled) setPhotoUrl(ok ? account.profile_picture_url : userImg);
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [account?.profile_picture_url]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        p: "12px",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        transition: "border-color 150ms ease-out",
        "&:hover": { borderColor: "#cbd5e1" },
      }}
    >
      <Box
        component="img"
        src={photoUrl}
        alt={account.username}
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          objectFit: "cover",
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}
        >
          {account.name || "Instagram User"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          @{account.username}
        </Typography>
      </Box>
      {isActive ? (
        <Badge variant="success">Active</Badge>
      ) : (
        <Button variant="ghost" size="sm" onClick={onSelect}>
          Switch to
        </Button>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={onReconnect}
        loading={reconnecting}
      >
        Reconnect
      </Button>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Notifications section
// -----------------------------------------------------------------------------
function NotificationsSection() {
  const [prefs, setPrefs] = useState({
    emailOnFailure: true,
    emailWeeklySummary: true,
    inAppNotifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await callNonStoreApiNew(
          "get",
          FetchNotificationsPrefs
        );
        if (cancelled) return;
        if (response.status === 200 && response.data) {
          setPrefs({
            emailOnFailure: !!response.data.emailOnFailure,
            emailWeeklySummary: !!response.data.emailWeeklySummary,
            inAppNotifications: !!response.data.inAppNotifications,
          });
        }
      } catch (err) {
        console.error("fetchNotificationsPrefs failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (key) => () =>
    setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const response = await callNonStoreApiNew(
        "put",
        UpdateNotificationsPrefs,
        prefs
      );
      if (response.status === 200 && response.data?.success) {
        enqueueSnackbar("Preferences saved", { variant: "success" });
      } else {
        enqueueSnackbar("Couldn't save preferences", { variant: "error" });
      }
    } catch (err) {
      console.error("updateNotificationsPrefs failed:", err);
      enqueueSnackbar("Couldn't save preferences", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    {
      key: "emailOnFailure",
      title: "DM failure emails",
      description: "Send me an email when an automation fails to send a DM.",
    },
    {
      key: "emailWeeklySummary",
      title: "Weekly summary emails",
      description: "Send a weekly recap of contacts, DMs and engagement.",
    },
    {
      key: "inAppNotifications",
      title: "In-app notifications",
      description: "Show alerts in the dashboard for automation events.",
    },
  ];

  return (
    <Card padding="lg">
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
        Notifications
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
        Choose which alerts you want to receive.
      </Typography>

      <Stack divider={<Box sx={{ height: "1px", bgcolor: "#e2e8f0" }} />} sx={{ mt: 2 }}>
        {rows.map((r) => (
          <Box
            key={r.key}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              py: "16px",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 14, fontWeight: 500, color: "#0f172a" }}
              >
                {r.title}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.25 }}>
                {r.description}
              </Typography>
            </Box>
            <Switch
              checked={prefs[r.key]}
              onChange={toggle(r.key)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#f97316",
                  "&:hover": { backgroundColor: "rgba(249,115,22,0.08)" },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#f97316",
                },
              }}
            />
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="primary"
          size="md"
          onClick={handleSave}
          disabled={loading || saving}
          loading={saving}
        >
          Save preferences
        </Button>
      </Box>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Billing section — link-out to existing credits page
// -----------------------------------------------------------------------------
function BillingSection() {
  const navigate = useNavigate();
  const { credentials } = useSelector((state) => state.instagram);

  const target = INSTAGRAM_CREDITS;

  return (
    <Card padding="lg">
      <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
        Billing
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
        View your plan, credits, and invoices.
      </Typography>
      <Box sx={{ mt: 3 }}>
        <Button
          variant="secondary"
          size="md"
          rightIcon={<ArrowRight size={16} />}
          onClick={() => navigate(target)}
        >
          Manage billing and subscription
        </Button>
      </Box>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Danger zone
// -----------------------------------------------------------------------------
function DangerSection() {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    if (deleting) return;
    try {
      setDeleting(true);
      const response = await callNonStoreApiNew("delete", DeleteAccount);
      if (response.status === 200 && response.data?.success) {
        const scheduled = response.data.deletionScheduledAt
          ? new Date(response.data.deletionScheduledAt).toLocaleDateString()
          : "in 30 days";
        enqueueSnackbar(
          `Account scheduled for deletion on ${scheduled}. Logging you out...`,
          { variant: "success", autoHideDuration: 5000 }
        );
        setConfirmOpen(false);
        setTimeout(() => {
          try {
            logout(getStoreRef());
          } catch (e) {
            console.error("logout failed after account deletion:", e);
            window.location.href = "/";
          }
        }, 3000);
      } else {
        enqueueSnackbar("Couldn't delete account", { variant: "error" });
      }
    } catch (err) {
      console.error("deleteAccount failed:", err);
      enqueueSnackbar("Couldn't delete account", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Card
        padding="lg"
        sx={{ border: "1px solid #fecaca", backgroundColor: "#fffafa" }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#b91c1c" }}>
          Danger zone
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.5 }}>
          Permanently delete your account and all associated data. This action
          cannot be undone.
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="danger"
            size="md"
            onClick={() => setConfirmOpen(true)}
          >
            Delete account
          </Button>
        </Box>
      </Card>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", maxWidth: 420 } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>
          Delete account?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "#475569" }}>
            Your ConvertDM account will be scheduled for deletion in 30 days.
            All automations will be paused immediately. To cancel deletion,
            contact support before then.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: "16px 24px" }}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={handleDeleteAccount}
            loading={deleting}
            disabled={deleting}
          >
            Delete account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
